import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Tabs, Checkbox, Input, message, Spin, Empty, List } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ApiService from '../../services/api';

interface AddFiatsSettingsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddFiatsSettingsModal: React.FC<AddFiatsSettingsModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [webFiats, setWebFiats] = useState<string[]>([]);
  const [existingFiats, setExistingFiats] = useState<string[]>([]);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    if (visible) {
      fetchData();
      setCheckedList([]);
      setSearchText('');
    }
  }, [visible]);

  // Clear selections when tab changes
  useEffect(() => {
    setCheckedList([]);
    setSearchText('');
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [webData, existingData] = await Promise.all([
        ApiService.getFiatsFromAllWebExchanges(),
        ApiService.getAllFiats(),
      ]);
      setWebFiats(webData);
      setExistingFiats(existingData);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Ошибка загрузки данных о валютах');
    } finally {
      setLoading(false);
    }
  };

  const newFiats = useMemo(() => {
    const existingSet = new Set(existingFiats);
    return webFiats.filter(f => !existingSet.has(f));
  }, [webFiats, existingFiats]);

  const currentDataSource = activeTab === '1' ? newFiats : existingFiats;

  const filteredData = useMemo(() => {
      return currentDataSource.filter(item => 
        item.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [currentDataSource, searchText]);

  const handleOk = async () => {
    if (checkedList.length === 0) {
      message.warning('Выберите хотя бы одну валюту');
      return;
    }
    setLoading(true);
    try {
      await ApiService.addFiatsInfo(checkedList);
      message.success('Валюты успешно добавлены');
      onSuccess();
    } catch (error) {
      console.error('Error adding fiats:', error);
      message.error('Не удалось добавить валюты');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (value: string) => {
    const currentIndex = checkedList.indexOf(value);
    const newChecked = [...checkedList];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedList(newChecked);
  };
  
  const handleSelectAll = (e: any) => {
      if (e.target.checked) {
          setCheckedList(filteredData);
      } else {
           setCheckedList([]);
      }
  }


  const renderContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '400px' }}>
       <div style={{ marginBottom: 16 }}>
          <Input 
             placeholder="Поиск валют" 
             prefix={<SearchOutlined />} 
             value={searchText}
             onChange={e => setSearchText(e.target.value)} 
          />
       </div>
       <div style={{ marginBottom: 8 }}>
            <Checkbox 
                checked={filteredData.length > 0 && checkedList.length === filteredData.length}
                indeterminate={checkedList.length > 0 && checkedList.length < filteredData.length}
                onChange={handleSelectAll}
                disabled={filteredData.length === 0}
            >
                Выбрать все
            </Checkbox>
            <span style={{ marginLeft: 8 }}>
                ({checkedList.length} выбрано)
            </span>
       </div>
       
       <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
          {filteredData.length > 0 ? (
            <List
                dataSource={filteredData}
                renderItem={(item) => (
                    <List.Item style={{ padding: '8px 0' }}>
                        <Checkbox 
                            checked={checkedList.includes(item)}
                            onChange={() => handleToggle(item)}
                        >
                            {item}
                        </Checkbox>
                    </List.Item>
                )}
            />
          ) : (
            <Empty description="Валюты не найдены" />
          )}
       </div>
    </div>
  );

  return (
    <Modal
      title="Настройки валют"
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      width={700}
      confirmLoading={loading}
      okText="Добавить"
      cancelText="Отмена"
    >
      <Spin spinning={loading}>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
                { key: '1', label: 'Добавление валют', children: renderContent() },
                { key: '2', label: 'Поиск новых бирж для старых валют', children: renderContent() }
            ]}
          />
      </Spin>
    </Modal>
  );
};

export default AddFiatsSettingsModal;
